<template>
    
    <Section :options="sectionOptions">

            <form id="profile-form" name="profile-form" class="profile-form mb-0" action="" method="post" @submit="onProfileFormSubmit">

                <h2>{{labels.title}}</h2>

                <validation-observer v-slot="{ invalid }" slim>
                    <div class="row">

                        <TextField 
                            v-bind:value.sync="form.profile.firstName" 
                            id="firstname" 
                            :label="labels.firstname" 
                            :placeholder="labels.firstname_placeholder" 
                            :rowClass="{'col-lg-6':true, 'form-group':true}" 
                            :inputClass="{'form-control':true, 'sm-form-control':true}" 
                            required 
                        />

                        <TextField 
                            v-bind:value.sync="form.profile.lastName" 
                            id="lastname" 
                            :label="labels.lastname" 
                            :placeholder="labels.lastname_placeholder" 
                            :rowClass="{'col-lg-6':true, 'form-group':true}" 
                            :inputClass="{'form-control':true, 'sm-form-control':true}" 
                            required 
                        />

                        <div class="w-100"></div>

                        <Address 
                            v-bind:value.sync="form.profile.address" 
                            :options="addressOptions"
                            :labels="addressLabels"
                            :inputClass="{'form-control':true, 'sm-form-control':true}" 
                            :rowClass="{'col-12':true, 'form-group':true}" 
                            required
                        />

                        <Phone 
                            id="phone" 
                            v-bind:value.sync="form.profile.phones[0]" 
                            :label="labels.phone" 
                            :rowClass="{'col-12':true, 'form-group':true}" 
                            :inputClass="{'form-control':true, 'sm-form-control':true}" 
                            required 
                        />

                        <CmsComponent v-if="components && components.meta" :component="components.meta" v-bind:value.sync="form.profile.meta" ></CmsComponent>

                        <div class="col-12 form-group">
                            <button :disabled="invalid" class="button button-3d button-black m-0 ladda-button" data-style="zoom-in" id="profile-form-submit" name="profile-form-submit" value="profile" type="submit">{{labels.button}}</button>
                        </div>
                    </div>
                </validation-observer>

            </form>

    </Section>

</template>

<style>

.profile-form {
    min-height:50vh;
    max-width:800px;
    margin-left:auto;
    margin-right:auto;
}

</style>

<script lang="ts">
import { defineComponent, PropType, computed, onMounted, onBeforeUnmount, ref } from '@fwk-node-modules/vue'
import TextField from '@fwk-client/components/panels/input/TextField.vue';
import Address from '@fwk-client/components/panels/input/Address.vue';
import Phone from '@fwk-client/components/panels/input/Phone.vue';
import CheckboxRow from '@fwk-client/components/panels/input/CheckboxRow.vue';
import Select from '@fwk-client/components/panels/input/Select.vue';
import Section, { SectionOptions } from '@root/src/client/components/panels/layouts/canvas/Section.vue';
import { getApp, useRouter, useStore } from '@fwk-client/utils/vue-3-migration';
import * as api from '@fwk-client/utils/api';
import * as Ladda from 'ladda';
import { authenticationTypes, languagesTypes } from '@fwk-client/store/types';
import { Component } from '@fwk-client/modules/cms/helpers/components';

/** @cmsOptions */
export interface ProfileOptions {
    /** @cmsType CmsInterface */
    sectionOptions?:SectionOptions
}

/** @cmsLabels */
export interface ProfileLabels {
    
}

/** @cmsSlots */
export interface ProfileSlots {
    meta?:Component
}

export default defineComponent({
    props: {
        options: Object as PropType<ProfileOptions>,
        labels: {
          type: Object as PropType<ProfileLabels>,
          default: () => { return {} }
        },
        components: Object as PropType<ProfileSlots>
    },
    components: {
        Section,
        TextField,
        Address,
        Phone,
        CheckboxRow,
        Select
    },
    setup(props, context) {
        const app = getApp();
        const $router = useRouter();
        const $store = useStore();

        const user = $store.getters['authentication/' + authenticationTypes.getters.GET_USER];

        const form = {
            profile: {...user.profile},
        }

        const addressOptions = {
            showName: false,
            showNote: false,
            labelAsColumn: false,
            nbAddressLines: 2,
            listCountries: "WORLD"
        }

        const addressLabels = {
            addressLines:[
                app.$t("authentication.register.form.address.addressLine")
            ],
            addressLines_placeholder:[
                app.$t("authentication.register.form.address.addressLine1_placeholder"),
                app.$t("authentication.register.form.address.addressLine2_placeholder")
            ],
            postalCode:app.$t("authentication.register.form.address.postalCode"),
            postalCode_placeholder:app.$t("authentication.register.form.address.postalCode_placeholder"),
            city:app.$t("authentication.register.form.address.city"),
            city_placeholder:app.$t("authentication.register.form.address.city_placeholder"),
            country:app.$t("authentication.register.form.address.country"),
            country_select:app.$t("authentication.register.form.address.country_select")
        }

        var laddaSubmit:Ladda.LaddaButton|null = null;

        const labels = {
            title: app.$t('authentication.profile.title'),
            firstname: app.$t('authentication.register.form.firstname'),
            firstname_placeholder: app.$t('authentication.register.form.firstname_placeholder'),
            lastname: app.$t('authentication.register.form.lastname'),
            lastname_placeholder: app.$t('authentication.register.form.lastname_placeholder'),
            phone: app.$t('authentication.register.form.phone'),
            button: app.$t('authentication.profile.button')
        }
        

        const computedOptions:ProfileOptions = {
            sectionOptions : {
                
            },
            ...props.options
        };

        const onProfileFormSubmit = (evt:Event) => {
            evt.preventDefault();

            var options:api.ApiVueOptions =  {
                app
            }

            var input = {
                "userID" : user._id,
                ...form
            }

            laddaSubmit!.start();
            
            api.postAPI('/api/auth/profile/update', input, options).then((response:any) => {

                if(response.updated && response.user) {
                    // We update current user
                    $store.commit('authentication/' + authenticationTypes.mutations.UPDATE_USER, response.user);
                }

                laddaSubmit!.stop();
            });
        }

        onMounted(() => {
            var button:HTMLButtonElement|null = document.querySelector( 'form[name=profile-form] button.ladda-button' );
            laddaSubmit = Ladda.create(button!);
        })

        onBeforeUnmount(() => {
        })

        return {
            sectionOptions:computedOptions.sectionOptions,
            form,
            labels,
            addressLabels,
            addressOptions,
            onProfileFormSubmit
        }

    }
})
</script>